<template>
  <div>
    <div class="page-header-row">
      <h4 class="table-page-header">Reports</h4>
      <el-button
        v-if="!isQuarterlyReport && reportData.length > 0"
        type="success"
        @click="downloadCSV"
        :loading="loadingDownload"
        class="download-btn"
      >
        Download CSV
      </el-button>
    </div>

    <!-- Filters Section -->
    <div class="filters-container">
      <div class="filter-group-compact">
        <AIVSelect
          v-model="selectedReportType"
          :options="reportTypeOptions"
          placeholder="Select Report"
          :searchable="false"
          :clearable="false"
          class="compact-select"
        />
      </div>
      <div v-if="showDateFilters" class="filter-group-compact">
        <AIVSelect
          v-model="dateRangeType"
          :options="dateRangeTypeOptions"
          placeholder="Period"
          :searchable="false"
          :clearable="false"
          class="compact-select"
          @input="handleDateRangeTypeChange"
        />
      </div>

      <!-- Custom Date Range Picker -->
      <div v-if="showDateFilters && dateRangeType === 'custom'" class="filter-group-compact date-group">
        <el-date-picker
          v-model="filters.dateRange"
          type="daterange"
          range-separator="-"
          start-placeholder="Start date"
          end-placeholder="End date"
          format="yyyy-MM-dd"
          value-format="yyyy-MM-dd"
          class="compact-date-picker"
        />
      </div>

      <!-- Period-based Date Picker with Navigation -->
      <template v-if="showDateFilters && dateRangeType !== 'custom'">
        <div class="filter-group-compact date-group date-nav-group">
          <button @click="goToPrevious" class="compact-nav-btn">
            <i class="fas fa-chevron-left"></i>
          </button>
          <el-date-picker
            ref="datePicker"
            v-model="selectedDate"
            :type="datePickerType"
            :format="dateFormat"
            :placeholder="placeholderText"
            class="compact-date-picker"
            :value-format="'yyyy-MM-dd'"
          />
          <button @click="goToNext" class="compact-nav-btn">
            <i class="fas fa-chevron-right"></i>
          </button>
        </div>
      </template>

      <!-- Force line break -->
      <div v-if="showSecondRowFilters" class="filter-break"></div>
      <div v-if="showPayerFilter" class="filter-group-compact">
        <AIVSelect
          v-model="filters.payers"
          :options="payerOptions"
          placeholder="Payer"
          :clearable="true"
          :searchable="false"
          :multiSelect="true"
          class="compact-select"
        />
      </div>
      <div v-if="showOfficeFilter" class="filter-group-compact">
        <AIVSelect
          v-model="filters.officeId"
          :options="officeOptions"
          placeholder="Office"
          :clearable="true"
          :searchable="true"
          :multiSelect="true"
          class="compact-select"
        />
      </div>
      <div v-if="showCaregiverFilter" class="filter-group-compact">
        <AIVSelect
          v-model="filters.caregiverId"
          :options="caregiverOptions"
          placeholder="Caregiver"
          :clearable="true"
          :searchable="true"
          :multiSelect="true"
          class="compact-select"
        />
      </div>
      <div v-if="showCaregiverAdminFilter" class="filter-group-compact">
        <AIVSelect
          v-model="filters.caregiverAdminId"
          :options="adminOptions"
          placeholder="Caregiver Admin"
          :clearable="true"
          :searchable="true"
          :multiSelect="true"
          class="compact-select"
        />
      </div>
      <div v-if="showSecondRowFilters" class="filter-group-compact">
        <AIVSelect
          v-model="filters.clientId"
          :options="activeClientOptions"
          placeholder="Client"
          :clearable="true"
          :searchable="true"
          :multiSelect="true"
          class="compact-select"
        />
      </div>
      <div v-if="showClientAdminFilter" class="filter-group-compact">
        <AIVSelect
          v-model="filters.clientAdminId"
          :options="adminOptions"
          placeholder="Client Admin"
          :clearable="true"
          :searchable="true"
          :multiSelect="true"
          class="compact-select"
        />
      </div>
      <div v-if="showServiceCodeFilter" class="filter-group-compact">
        <AIVSelect
          v-model="filters.serviceCodeIds"
          :options="serviceCodeOptions"
          placeholder="Service Code"
          :clearable="true"
          :searchable="true"
          :multiSelect="true"
          class="compact-select"
        />
      </div>

      <!-- Timesheet Billing: approver email goes into every CSV row -->
      <div v-if="selectedReportType === 'timesheet-billing'" class="filter-group-compact">
        <el-input
          v-model="timesheetApproverEmail"
          placeholder="Approver email"
          class="compact-select"
          @change="onApproverEmailChange"
        />
      </div>

      <!-- Quarterly Submission Tracker Filters -->
      <div v-if="isQuarterlySubmission" class="filter-group-compact">
        <AIVSelect
          v-model="submissionSelectedQuarter"
          :options="quarterOptions"
          placeholder="Select Quarter"
          :searchable="false"
          :clearable="false"
          class="compact-select"
        />
      </div>
      <div v-if="isQuarterlySubmission" class="filter-group-compact">
        <AIVSelect
          v-model="submissionFilters.clientId"
          :options="dspdPrivateClientOptions"
          placeholder="Client"
          :clearable="true"
          :searchable="true"
          :multiSelect="true"
          class="compact-select"
        />
      </div>
      <div v-if="isQuarterlySubmission" class="filter-group-compact">
        <AIVSelect
          v-model="submissionFilters.payerType"
          :options="dspdPrivatePayerOptions"
          placeholder="Payer"
          :clearable="true"
          :searchable="false"
          :multiSelect="true"
          class="compact-select"
        />
      </div>
      <div v-if="isQuarterlySubmission" class="filter-group-compact">
        <AIVSelect
          v-model="submissionFilters.supportCoordinatorId"
          :options="supportCoordinatorOptions"
          placeholder="Support Coordinator"
          :clearable="true"
          :searchable="true"
          :multiSelect="true"
          class="compact-select"
        />
      </div>
      <div v-if="isQuarterlySubmission" class="filter-group-compact">
        <AIVSelect
          v-model="submissionFilters.status"
          :options="submissionStatusOptions"
          placeholder="Status"
          :clearable="true"
          :searchable="false"
          :multiSelect="true"
          class="compact-select"
        />
      </div>

      <!-- Quarterly Report Filters -->
      <div v-if="isQuarterlyReport" class="filter-group-compact">
        <AIVSelect
          v-model="quarterlyClientId"
          :options="dspdClientOptions"
          placeholder="Select Client"
          :searchable="true"
          :clearable="true"
          class="compact-select"
        />
      </div>
      <div v-if="isQuarterlyReport" class="filter-group-compact">
        <AIVSelect
          v-model="selectedQuarter"
          :options="quarterOptions"
          placeholder="Select Quarter"
          :searchable="false"
          :clearable="true"
          class="compact-select"
        />
      </div>
      <div v-if="isQuarterlyReport && quarterlyServiceCodeOptions.length > 0" class="filter-group-compact">
        <AIVSelect
          v-model="quarterlySelectedServiceCodes"
          :options="quarterlyServiceCodeOptions"
          placeholder="Select Service Codes"
          :searchable="false"
          :clearable="true"
          :multiSelect="true"
          class="compact-select"
        />
      </div>
      <div v-if="isQuarterlyReport && quarterlyServiceCodeOptions.length > 0" class="filter-group-compact">
        <el-button
          type="success"
          @click="quarterlyGenerateCounter++"
          :loading="quarterlyGenerating"
          :disabled="quarterlySelectedServiceCodes.length === 0"
        >
          <i class="fa fa-magic" style="margin-right: 6px"></i>
          Generate Report
        </el-button>
      </div>
      <div v-if="isQuarterlyReport && quarterlyClientId && selectedQuarter" class="filter-group-compact">
        <el-button
          type="info"
          @click="quarterlyUploadCounter++"
          :loading="quarterlyUploading"
        >
          <i class="fa fa-upload" style="margin-right: 6px"></i>
          Upload Report PDF
        </el-button>
      </div>
    </div>

    <!-- Quarterly Report Component -->
    <QuarterlyReport
      v-if="isQuarterlyReport && quarterlyClientId && selectedQuarter"
      :embedded="true"
      :externalClientId="quarterlyClientId"
      :externalDateRange="quarterlyDateRange"
      :externalServiceCodes="quarterlySelectedServiceCodes"
      :triggerGenerate="quarterlyGenerateCounter"
      :triggerUpload="quarterlyUploadCounter"
      @service-codes-loaded="quarterlyServiceCodeOptions = $event"
      @generating="quarterlyGenerating = $event"
      @uploading="quarterlyUploading = $event"
    />

    <!-- Timesheet billing data warnings: fix these before uploading to the state -->
    <div v-if="selectedReportType === 'timesheet-billing' && timesheetWarnings.length > 0" class="timesheet-warnings mt-3">
      <div class="timesheet-warnings-header">
        <i class="fas fa-exclamation-triangle"></i>
        {{ timesheetWarnings.length }} data issue{{ timesheetWarnings.length === 1 ? '' : 's' }} to review before uploading
      </div>
      <ul>
        <li v-for="(w, i) in timesheetWarnings" :key="'tw-' + i">{{ w }}</li>
      </ul>
    </div>

    <!-- Report Results -->
    <div v-if="!isQuarterlyReport && reportData.length > 0" class="mt-4">
      <AIVTable
        :columns="currentReportColumns"
        :data="reportData"
        :pagination="pagination"
        :item-name="'records'"
        :row-class="selectedReportType === 'monthly-billing' ? billingRowClass : null"
        @pageChange="handlePageChange"
        @pageSizeChange="handlePageSizeChange"
        @sort-change="handleSortChange"
      >
        <template #cell-question_responses="{ row }">
          <el-button
            v-if="(row.question_responses && row.question_responses.length > 0) || row.shift_notes || row.notes"
            size="mini"
            type="primary"
            @click="openResponsesModal(row)"
          >View</el-button>
          <span v-else class="text-muted">No responses</span>
        </template>
        <template #cell-caregiver_name="{ row }">
          <el-tooltip
            v-if="row.is_group_shift && row.caregiver_count > 1"
            placement="bottom"
            effect="light"
            :open-delay="200"
          >
            <div slot="content" v-html="getCaregiverTooltipContent(row)"></div>
            <span class="multiple-indicator">{{ row.caregiver_name }}</span>
          </el-tooltip>
          <span v-else>{{ row.caregiver_name }}</span>
        </template>
      </AIVTable>

      <AIVDialog
        v-model="showResponsesModal"
        title="Question Responses"
        width="600px"
        height="70vh"
        :footer="false"
        class="responses-modal"
      >
        <div class="responses-modal-body">
          <div
            v-for="(item, idx) in parsedResponses"
            :key="idx"
            class="question-answer-row"
          >
            <div class="question-text">{{ item.questionText }}</div>
            <div
              class="answer-text"
              :class="{ 'answer-yes': item.answer === 'yes', 'answer-no': item.answer === 'no' }"
            >{{ item.answer }}</div>
          </div>
        </div>
      </AIVDialog>
    </div>

  </div>
</template>

<script>
import { reportsService } from '@/serviceHandlers/reportsService';
import { officesService } from '@/serviceHandlers/officesService';
import { caregiversService } from '@/serviceHandlers/caregiversService';
import { clientsService } from '@/serviceHandlers/clientsService';
import { serviceCodesService } from '@/serviceHandlers/serviceCodesService';
import { adminUsersService } from '@/serviceHandlers/adminUsersService';
import QuarterlyReport from '@/pages/QuarterlyReport.vue';

export default {
  name: 'Reports',
  components: {
    QuarterlyReport,
  },
  data() {
    return {
      selectedReportType: 'monthly-billing',
      reportTypeOptions: [
        { id: 'monthly-billing', name: 'Monthly Billing Report By Client' },
        { id: 'timesheet-billing', name: 'Timesheet Billing Report' },
        { id: 'monthly-billing-simple', name: 'Payroll Hours Report' },
        { id: 'active-auths-no-shifts', name: 'Unserved Active Clients' },
        { id: 'auths-with-approved-shifts', name: 'Auths with Approved Shifts' },
        { id: 'dspd-shift-notes', name: 'DSPD Shift Notes' },
        { id: 'dspd-consolidated', name: 'DSPD Consolidated Billing (DSG/DSP/MTP/DSI)' },
        { id: 'pba-billing', name: 'PBA Billing Report' },
        { id: 'hours-units-consolidated', name: 'Consolidated Billing (CMP/SLN/SLH/SEI)' },
        { id: 'all-dspd', name: 'All DSPD Billing' },
        { id: 'vr-auth-consolidation', name: 'VR Auth Consolidation Report' },
        { id: 'monthly-units', name: 'Monthly Units Report' },
        { id: 'attendance', name: 'Attendance Report' },
        { id: 'projection-vs-actual', name: 'Projection vs. Actual (VR/DSPD)' },
        { id: 'evv', name: 'EVV Report' },
        { id: 'mileage', name: 'Mileage Report' },
        { id: 'quarterly-report', name: 'Quarterly Report' },
        { id: 'quarterly-submission', name: 'Quarterly Submission Tracker' },
        { id: 'client-compliance', name: 'Client Document Compliance' },
      ],
      timesheetApproverEmail: localStorage.getItem('timesheetApproverEmail') || '',
      timesheetWarnings: [],
      dateRangeType: 'month',
      dateRangeTypeOptions: [
        { id: 'month', name: 'Month' },
        { id: 'two-weeks', name: 'Two Weeks' },
        { id: 'week', name: 'Week' },
        { id: 'day', name: 'Day' },
        { id: 'custom', name: 'Custom' }
      ],
      selectedDate: null,
      filters: {
        dateRange: null,
        officeId: [],
        caregiverId: [],
        clientId: [],
        clientAdminId: [],
        caregiverAdminId: [],
        serviceCodeIds: [],
        payers: [],
      },
      payerOptions: [
        { id: 'VR',   name: 'VR'   },
        { id: 'DSPD', name: 'DSPD' },
        { id: 'Private', name: 'Private Pay' },
      ],
      officeOptions: [],
      caregiverOptions: [],
      clientOptions: [],
      dspdClientOptions: [],
      dspdPrivateClientOptions: [],
      supportCoordinatorOptions: [],
      adminOptions: [],
      serviceCodeOptions: [],
      sortProp: '',
      sortOrder: '',
      reportData: [],
      mileageAggregations: { byCaregiver: [], byManager: [] },
      reportColumns: [
        { label: 'Date', key: 'shift_date', sortable: true, format: this.formatTableDate },
        { label: 'Auth Number', key: 'auth_number', sortable: true },
        { label: 'Caregiver', key: 'caregiver_name', sortable: true },
        { label: 'Client', key: 'client_name', sortable: true },
        { label: 'Service Code', key: 'service_code', sortable: true },
        { label: 'Office', key: 'office_name', sortable: true },
        { label: 'Hours', key: 'hours', sortable: true },
        { label: 'Units', key: 'units', sortable: true },
        { label: 'Rate', key: 'rate', sortable: true },
        { label: 'Total', key: 'total', sortable: true },
      ],
      pagination: {
        currentPage: 1,
        perPage: 20,
        total: 0,
        totalPages: 1,
      },
      loadingReport: false,
      loadingDownload: false,
      showResponsesModal: false,
      selectedResponsesRow: null,
      // Quarterly Report
      quarterlyClientId: null,
      selectedQuarter: null,
      quarterlyServiceCodeOptions: [],
      quarterlySelectedServiceCodes: [],
      quarterlyGenerateCounter: 0,
      quarterlyGenerating: false,
      quarterlyUploadCounter: 0,
      quarterlyUploading: false,
      // Quarterly Submission Tracker
      submissionSelectedQuarter: null,
      submissionFilters: {
        clientId: [],
        payerType: [],
        supportCoordinatorId: [],
        status: [],
      },
      dspdPrivatePayerOptions: [
        { id: 'DSPD', name: 'DSPD' },
        { id: 'Private', name: 'Private Pay' },
      ],
      submissionStatusOptions: [
        { id: 'Missing', name: 'Missing' },
        { id: 'Draft', name: 'Draft' },
        { id: 'Final - Not Sent', name: 'Final - Not Sent' },
        { id: 'Sent', name: 'Sent' },
      ],
    };
  },
  computed: {
    datePickerType() {
      switch (this.dateRangeType) {
        case 'day':
          return 'date';
        case 'week':
        case 'two-weeks':
          return 'week';
        case 'month':
          return 'month';
        default:
          return 'date';
      }
    },
    dateFormat() {
      switch (this.dateRangeType) {
        case 'day':
          return 'MMMM d, yyyy';
        case 'week':
        case 'two-weeks':
          return 'MMMM d, yyyy';
        case 'month':
          return 'MMMM yyyy';
        default:
          return 'MMMM d, yyyy';
      }
    },
    placeholderText() {
      switch (this.dateRangeType) {
        case 'day':
          return 'Select date';
        case 'week':
          return 'Select week';
        case 'two-weeks':
          return 'Select week';
        case 'month':
          return 'Select month';
        default:
          return 'Select date';
      }
    },
    dateRangeDisplay() {
      if (!this.selectedDate) return "";

      let date;
      if (this.selectedDate instanceof Date) {
        date = new Date(this.selectedDate.getTime());
      } else if (typeof this.selectedDate === "string") {
        // Parse as local date to avoid timezone issues
        const [year, month, day] = this.selectedDate.split('-').map(Number);
        date = new Date(year, month - 1, day);
      } else {
        return "";
      }

      if (isNaN(date.getTime())) return "";

      const monthNamesFull = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
      const monthNamesShort = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

      try {
        switch (this.dateRangeType) {
          case 'day':
            return `${monthNamesFull[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;

          case 'week':
            const dayOfWeek = date.getDay();
            const sunday = new Date(date.getTime());
            sunday.setDate(date.getDate() - dayOfWeek);
            const saturday = new Date(sunday.getTime());
            saturday.setDate(sunday.getDate() + 6);

            const sundayStr = `${dayNames[0]}, ${monthNamesShort[sunday.getMonth()]} ${sunday.getDate()}, ${sunday.getFullYear()}`;
            const saturdayStr = `${dayNames[6]}, ${monthNamesShort[saturday.getMonth()]} ${saturday.getDate()}, ${saturday.getFullYear()}`;

            return `${sundayStr} - ${saturdayStr}`;

          case 'two-weeks':
            const dayOfWeek2 = date.getDay();
            const startSunday = new Date(date.getTime());
            startSunday.setDate(date.getDate() - dayOfWeek2);
            const endSaturday = new Date(startSunday.getTime());
            endSaturday.setDate(startSunday.getDate() + 13);

            const startStr = `${dayNames[0]}, ${monthNamesShort[startSunday.getMonth()]} ${startSunday.getDate()}, ${startSunday.getFullYear()}`;
            const endStr = `${dayNames[6]}, ${monthNamesShort[endSaturday.getMonth()]} ${endSaturday.getDate()}, ${endSaturday.getFullYear()}`;

            return `${startStr} - ${endStr}`;

          case 'month':
            return `${monthNamesFull[date.getMonth()]} ${date.getFullYear()}`;

          default:
            return date.toLocaleDateString();
        }
      } catch (error) {
        return "Invalid Date";
      }
    },
    showDateFilters() {
      return !this.isQuarterlyReport && !this.isQuarterlySubmission;
    },
    isQuarterlyReport() {
      return this.selectedReportType === 'quarterly-report';
    },
    isQuarterlySubmission() {
      return this.selectedReportType === 'quarterly-submission';
    },
    quarterOptions() {
      const options = [];
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth(); // 0-indexed
      const currentQuarter = Math.floor(currentMonth / 3) + 1;

      // Start from Q1 2026 up to the current quarter
      const startYear = 2026;
      for (let year = startYear; year <= currentYear; year++) {
        const maxQ = year === currentYear ? currentQuarter : 4;
        for (let q = 1; q <= maxQ; q++) {
          if (year === startYear || q <= maxQ) {
            options.push({
              id: `${year}-Q${q}`,
              name: `Q${q} ${year}`,
            });
          }
        }
      }
      return options;
    },
    quarterlyDateRange() {
      if (!this.selectedQuarter) return null;
      const [year, qStr] = this.selectedQuarter.split('-');
      const q = parseInt(qStr.replace('Q', ''));
      const startMonth = (q - 1) * 3;
      const startDate = `${year}-${String(startMonth + 1).padStart(2, '0')}-01`;
      const endMonth = startMonth + 3;
      const endDate = endMonth > 12
        ? `${parseInt(year) + 1}-01-01`
        : `${year}-${String(endMonth + 1).padStart(2, '0')}-01`;
      // Last day of quarter
      const lastDay = new Date(parseInt(year), startMonth + 3, 0).getDate();
      const endDateFinal = `${year}-${String(startMonth + 3).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
      return [startDate, endDateFinal];
    },
    isNewConsolidatedReport() {
      return ['dspd-consolidated', 'pba-billing', 'hours-units-consolidated', 'all-dspd', 'vr-auth-consolidation'].includes(this.selectedReportType);
    },
    showSecondRowFilters() {
      return this.selectedReportType !== 'active-auths-no-shifts' &&
             this.selectedReportType !== 'auths-with-approved-shifts' &&
             !this.isQuarterlyReport &&
             !this.isQuarterlySubmission;
    },
    showOfficeFilter() {
      return this.showSecondRowFilters && this.selectedReportType !== 'dspd-shift-notes' && !this.isNewConsolidatedReport;
    },
    showCaregiverFilter() {
      return this.showSecondRowFilters && this.selectedReportType !== 'dspd-shift-notes' && !this.isNewConsolidatedReport && this.selectedReportType !== 'client-compliance';
    },
    showCaregiverAdminFilter() {
      return this.showSecondRowFilters && this.selectedReportType !== 'dspd-shift-notes' && !this.isNewConsolidatedReport && this.selectedReportType !== 'client-compliance';
    },
    showClientAdminFilter() {
      return this.showSecondRowFilters && this.selectedReportType !== 'dspd-shift-notes' && !this.isNewConsolidatedReport;
    },
    showServiceCodeFilter() {
      return this.showSecondRowFilters && this.selectedReportType !== 'dspd-shift-notes' && !this.isNewConsolidatedReport && this.selectedReportType !== 'client-compliance';
    },
    showPayerFilter() {
      return this.selectedReportType === 'monthly-billing' || this.selectedReportType === 'projection-vs-actual' || this.selectedReportType === 'evv' || this.selectedReportType === 'timesheet-billing';
    },
    activeClientOptions() {
      if (this.selectedReportType === 'dspd-shift-notes' || this.selectedReportType === 'dspd-consolidated' || this.selectedReportType === 'pba-billing' || this.selectedReportType === 'hours-units-consolidated' || this.selectedReportType === 'all-dspd') {
        return this.dspdClientOptions;
      }
      return this.clientOptions;
    },
    parsedResponses() {
      const responses = [...(this.selectedResponsesRow?.question_responses || [])];
      // Append the "Any Concerns" answer from shift_notes if available
      const concerns = this.selectedResponsesRow?.shift_notes || this.selectedResponsesRow?.notes;
      if (concerns) {
        responses.push({
          questionText: 'Do you have any concerns about this individual today?',
          answer: concerns,
        });
      }
      return responses;
    },
    currentReportColumns() {
      if (this.selectedReportType === 'dspd-shift-notes') {
        return [
          { label: 'Date',               key: 'shift_date',          sortable: true },
          { label: 'Client',             key: 'client_name',         sortable: true },
          { label: 'Service Code',       key: 'service_code',        sortable: true },
          { label: 'Pick Up',            key: 'pickup_info',         sortable: false, isHtml: true, format: (v) => v ? v.replace(/\n/g, '<br>') : v },
          { label: 'Drop Off',           key: 'dropoff_info',        sortable: false, isHtml: true, format: (v) => v ? v.replace(/\n/g, '<br>') : v },
          { label: 'Question Responses', key: 'question_responses',  sortable: false, isHtml: true, format: (v) => v ? v.replace(/\n/g, '<br>') : v },
          { label: 'Medications',        key: 'medication_responses', sortable: false, isHtml: true, format: (v) => v ? v.replace(/\n/g, '<br>') : v },
        ];
      }
      if (this.selectedReportType === 'active-auths-no-shifts') {
        return [
          { label: 'Client', key: 'client_name', sortable: true },
          { label: 'Office', key: 'office_name', sortable: true },
          { label: '# Active Auths', key: 'active_auth_count', sortable: true },
        ];
      }
      if (this.selectedReportType === 'auths-with-approved-shifts') {
        return [
          { label: 'Auth Number', key: 'auth_number', sortable: true },
          { label: 'Client', key: 'client_name', sortable: true },
          { label: 'Service Code', key: 'service_code', sortable: true },
          { label: 'Office', key: 'office_name', sortable: true },
        ];
      }
      if (this.selectedReportType === 'dspd-consolidated') {
        return [
          { label: 'Client', key: 'client_name', sortable: true },
          { label: 'DSG Units', key: 'dsg_units', sortable: true },
          { label: 'DSP Units', key: 'dsp_units', sortable: true },
          { label: 'MTP Units', key: 'mtp_units', sortable: true },
          { label: 'DSI Units', key: 'dsi_units', sortable: true },
          { label: 'Total Units', key: 'total_units', sortable: true },
        ];
      }
      if (this.selectedReportType === 'pba-billing') {
        return [
          { label: 'Client', key: 'client_name', sortable: true },
          { label: 'Units', key: 'units', sortable: true },
        ];
      }
      if (this.selectedReportType === 'hours-units-consolidated') {
        return [
          { label: 'Client', key: 'client_name', sortable: true },
          { label: 'Service Code', key: 'service_code', sortable: true },
          { label: 'Hours', key: 'hours', sortable: true },
          { label: 'Units', key: 'units', sortable: true },
        ];
      }
      if (this.selectedReportType === 'all-dspd') {
        return [
          { label: 'Client', key: 'client_name', sortable: true },
          { label: 'Service Code', key: 'service_code', sortable: true },
          { label: 'Hours', key: 'hours', sortable: true },
          { label: 'Units', key: 'units', sortable: true },
        ];
      }
      if (this.selectedReportType === 'vr-auth-consolidation') {
        return [
          { label: 'Auth Number', key: 'auth_number', sortable: true },
          { label: 'Client', key: 'client_name', sortable: true },
          { label: 'Office', key: 'office', sortable: true },
          { label: 'Service Code', key: 'service_code', sortable: true },
          { label: 'Total Hours', key: 'total_hours', sortable: true },
          { label: 'Rate', key: 'rate', sortable: true },
          { label: 'Total', key: 'total', sortable: true },
          { label: 'Date Range', key: 'date_range', sortable: true },
        ];
      }
      if (this.selectedReportType === 'monthly-units') {
        return [
          { label: 'Client', key: 'client_name', sortable: true },
          { label: 'DSG Units', key: 'dsg_units', sortable: true },
          { label: 'DSP Units', key: 'dsp_units', sortable: true },
          { label: 'MTP Units', key: 'mtp_units', sortable: true },
          { label: 'DSI Units', key: 'dsi_units', sortable: true },
          { label: 'Total Units', key: 'total_units', sortable: true },
        ];
      }
      if (this.selectedReportType === 'attendance') {
        return [
          { label: 'Client', key: 'client_name', sortable: true },
          { label: 'Program', key: 'program_name', sortable: true },
          { label: 'Total Days', key: 'total_days', sortable: true },
          { label: 'Present', key: 'present_count', sortable: true },
          { label: 'Absent', key: 'absent_count', sortable: true },
          { label: 'No Show', key: 'no_show_count', sortable: true },
          { label: 'Attendance %', key: 'attendance_pct', sortable: true },
        ];
      }
      if (this.selectedReportType === 'projection-vs-actual') {
        return [
          { label: 'Service<br>Code', key: 'service_code', sortable: true },
          { label: 'Payer', key: 'payer_type', sortable: true },
          { label: 'Scheduled<br>Hours', key: 'scheduled_hours', sortable: true },
          { label: 'Approved<br>Hours', key: 'approved_hours', sortable: true },
          { label: 'Variance', key: 'variance_hours', sortable: true },
          { label: 'Fulfillment<br>%', key: 'fulfillment_pct', sortable: true },
          { label: 'Scheduled<br>Units', key: 'scheduled_units', sortable: true },
          { label: 'Approved<br>Units', key: 'approved_units', sortable: true },
          { label: 'Total<br>Shifts', key: 'total_shifts', sortable: true },
          { label: 'Approved<br>Shifts', key: 'approved_shifts', sortable: true },
          { label: 'Missed<br>Shifts', key: 'missed_shifts', sortable: true },
        ];
      }
      if (this.selectedReportType === 'mileage') {
        return [
          { label: 'Date', key: 'shift_date', sortable: true },
          { label: 'Caregiver', key: 'caregiver_name', sortable: true },
          { label: 'Manager', key: 'manager_name', sortable: true },
          { label: 'Client', key: 'client_name', sortable: true },
          { label: 'Start Location', key: 'start_location' },
          { label: 'Places Visited', key: 'mid_location' },
          { label: 'End Location', key: 'end_location' },
          { label: 'Odometer Start', key: 'odometer_start', sortable: true },
          { label: 'Odometer End', key: 'odometer_end', sortable: true },
          { label: 'Total Miles', key: 'total_miles', sortable: true },
          { label: 'Approved', key: 'mileage_approved', sortable: true, format: (v) => (v ? 'Yes' : 'No') },
          { label: 'Approved Miles', key: 'approved_miles', sortable: true, format: (v) => (v != null ? v : '—') },
          { label: 'Reimbursement ($)', key: 'reimbursement', sortable: true, format: (v) => (v != null ? `$${Number(v).toFixed(2)}` : '—') },
          { label: 'Transporting', key: 'is_transporting', sortable: true, format: (v) => (v ? 'Yes' : 'No') },
        ];
      }
      if (this.selectedReportType === 'timesheet-billing') {
        // Mirrors the state upload columns exactly (see TIMESHEET_BILLING.md)
        return [
          { label: 'Line #', key: 'line_number', sortable: true },
          { label: 'Approver Email', key: 'provider_approver_email', sortable: false },
          { label: 'Consumer', key: 'consumer_name', sortable: true },
          { label: 'PID', key: 'consumer_pid', sortable: true },
          { label: 'Code', key: 'service_code', sortable: true },
          { label: 'Rate', key: 'rate', sortable: true },
          { label: 'Unit Type', key: 'unit_type', sortable: false },
          { label: 'Start Date', key: 'service_start_date', sortable: true },
          { label: 'End Date', key: 'service_end_date', sortable: false },
          { label: 'Units', key: 'units', sortable: true },
          { label: 'Start Time', key: 'service_start_time', sortable: false },
          { label: 'End Time', key: 'service_end_time', sortable: false },
          { label: 'Employee ID', key: 'employee_id', sortable: true },
        ];
      }
      if (this.selectedReportType === 'evv') {
        return [
          {
            label: 'First Name',
            key: 'first_name',
            sortable: true,
            // For group shifts the backend puts the client count in first_name
            // (e.g. "4"); individual shifts send a real first name. When the
            // value is purely numeric, render it as "N clients".
            format: (v) => {
              if (v == null || v === '') return v;
              return /^\d+$/.test(String(v).trim()) ? `${String(v).trim()} clients` : v;
            },
          },
          { label: 'Last Name', key: 'last_name', sortable: true },
          { label: 'Service Code', key: 'service_code', sortable: true },
          { label: 'Employee', key: 'employee', sortable: true },
          { label: 'Date', key: 'begin_date', sortable: true },
          { label: 'Begin Time', key: 'begin_time', sortable: true, format: this.formatEVVTime },
          { label: 'Begin Geocode', key: 'begin_geocode', sortable: false },
          { label: 'End Time', key: 'end_time', sortable: true, format: this.formatEVVTime },
          { label: 'End Geocode', key: 'end_geocode', sortable: false },
        ];
      }
      if (this.selectedReportType === 'quarterly-submission') {
        return [
          { label: 'Client', key: 'client_name', sortable: true },
          {
            label: 'Status', key: 'status', sortable: true,
            format: (v) => {
              if (v === 'Missing') return `<span style="color:#f44336;font-weight:bold">Missing</span>`;
              if (v === 'Final - Not Sent') return `<span style="color:#e6a23c;font-weight:bold">Final - Not Sent</span>`;
              if (v === 'Draft') return `<span style="color:#909399">Draft</span>`;
              if (v === 'Sent') return `<span style="color:#67c23a;font-weight:bold">Sent</span>`;
              return v;
            },
            isHtml: true,
          },
          { label: 'Service Codes', key: 'service_codes', sortable: false },
          { label: 'Report Created', key: 'created_date', sortable: true },
          { label: 'Emailed Date', key: 'emailed_date', sortable: true },
          { label: 'Emailed To', key: 'emailed_to', sortable: false },
          { label: 'Sent By', key: 'sent_by', sortable: false },
          { label: 'Support Coordinator', key: 'support_coordinator', sortable: true },
        ];
      }
      if (this.selectedReportType === 'client-compliance') {
        return [
          { label: 'Client', key: 'client_name', sortable: true },
          { label: 'Document Type', key: 'document_type', sortable: true },
          { label: 'Source', key: 'source', sortable: true },
          { label: 'Expiration Date', key: 'expiration_date', sortable: true },
          {
            label: 'Days Left', key: 'days_until_expiration', sortable: true,
            format: (v) => {
              if (v <= 0) return `<span style="color:#f44336;font-weight:bold">${v} (EXPIRED)</span>`;
              if (v <= 14) return `<span style="color:#e6a23c;font-weight:bold">${v}</span>`;
              return `<span style="color:#67c23a">${v}</span>`;
            },
            isHtml: true,
          },
          { label: 'Programs', key: 'programs', sortable: false },
          { label: 'Admin', key: 'admin_name', sortable: true },
          { label: 'Office', key: 'office_name', sortable: true },
        ];
      }
      if (this.selectedReportType === 'monthly-billing') {
        return [
          { label: 'Client', key: 'client_name', sortable: false },
          { label: 'Service Code', key: 'service_code', sortable: false },
          { label: 'Date', key: 'shift_date', sortable: false, format: this.formatTableDate },
          { label: 'Hours', key: 'hours', sortable: false },
        ];
      }
      if (this.selectedReportType === 'monthly-billing-simple') {
        return [
          { label: 'Date', key: 'shift_date', sortable: true, format: this.formatTableDate },
          { label: 'Auth Number', key: 'auth_number', sortable: true },
          { label: 'Caregiver', key: 'caregiver_name', sortable: true },
          { label: 'Client', key: 'client_name', sortable: true },
          { label: 'Service Code', key: 'service_code', sortable: true },
          { label: 'Office', key: 'office_name', sortable: true },
          { label: 'Hours', key: 'hours', sortable: true },
          { label: 'Rate', key: 'rate', sortable: true },
          { label: 'Total', key: 'total', sortable: true },
        ];
      }
      return this.reportColumns;
    },
    billingRowClass() {
      return (row) => {
        if (row._rowType === 'grandTotal') return 'billing-grand-total-row';
        if (row._rowType === 'subtotal') return 'billing-subtotal-row';
        return '';
      };
    },
  },
  mounted() {
    this.loadFilterOptions();
    // Initialize with current month
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    this.selectedDate = `${year}-${month}-${day}`;
    // Note: The selectedDate watcher will call updateDateRangeFromSelectedDate() and updateDatePickerDisplay()
  },
  watch: {
    selectedReportType: {
      handler(newValue) {
        this.reportData = [];
        this.timesheetWarnings = [];
        this.pagination.currentPage = 1;
        this.sortProp = '';
        this.sortOrder = '';
        // Reset quarterly report filters when switching away
        if (newValue !== 'quarterly-report') {
          this.quarterlyClientId = null;
          this.selectedQuarter = null;
          this.quarterlyServiceCodeOptions = [];
          this.quarterlySelectedServiceCodes = [];
          this.quarterlyGenerateCounter = 0;
          this.quarterlyUploadCounter = 0;
        }
        // Reset quarterly submission filters when switching away
        if (newValue !== 'quarterly-submission') {
          this.submissionSelectedQuarter = null;
          this.submissionFilters.clientId = [];
          this.submissionFilters.payerType = [];
          this.submissionFilters.supportCoordinatorId = [];
          this.submissionFilters.status = [];
        }
        if (newValue === 'quarterly-report') return;
        if (newValue === 'quarterly-submission') return;
        if (!(this.filters.dateRange && this.filters.dateRange.length === 2)) return;
        if (newValue === 'active-auths-no-shifts') {
          this.generateActiveAuthsNoShiftsReport();
        } else if (newValue === 'auths-with-approved-shifts') {
          this.generateAuthsWithApprovedShiftsReport();
        } else if (newValue === 'dspd-shift-notes') {
          this.generateDspdShiftNotesReport();
        } else if (newValue === 'dspd-consolidated') {
          this.generateDspdConsolidatedReport();
        } else if (newValue === 'pba-billing') {
          this.generatePbaBillingReport();
        } else if (newValue === 'hours-units-consolidated') {
          this.generateHoursUnitsConsolidatedReport();
        } else if (newValue === 'all-dspd') {
          this.generateAllDspdReport();
        } else if (newValue === 'vr-auth-consolidation') {
          this.generateVrAuthConsolidationReport();
        } else if (newValue === 'monthly-units') {
          this.generateMonthlyUnitsReport();
        } else if (newValue === 'attendance') {
          this.generateAttendanceReport();
        } else if (newValue === 'projection-vs-actual') {
          this.generateProjectionVsActualReport();
        } else if (newValue === 'mileage') {
          this.generateMileageReport();
        } else if (newValue === 'evv') {
          this.generateEVVReport();
        } else if (newValue === 'monthly-billing') {
          this.generateReport();
        } else if (newValue === 'timesheet-billing') {
          this.generateTimesheetBillingReport();
        } else if (newValue === 'monthly-billing-simple') {
          this.generateMonthlyBillingSimpleReport();
        } else if (newValue === 'client-compliance') {
          this.generateClientComplianceReport();
        } else if (newValue === 'quarterly-submission') {
          this.generateQuarterlySubmissionReport();
        }
      },
    },
    dateRangeType: {
      handler() {
        this.$nextTick(() => {
          this.updateDatePickerDisplay();
        });
      },
    },
    selectedDate: {
      handler(newValue) {
        if (newValue && this.dateRangeType !== 'custom') {
          this.updateDateRangeFromSelectedDate();
          this.$nextTick(() => {
            this.updateDatePickerDisplay();
          });
        }
      },
    },
    'filters.dateRange': {
      handler(newValue) {
        if (newValue && newValue.length === 2) {
          this.pagination.currentPage = 1;
          this.dispatchReportGeneration();
        }
      },
    },
    'filters.officeId': {
      handler() {
        if (this.filters.dateRange && this.filters.dateRange.length === 2) {
          this.pagination.currentPage = 1;
          this.dispatchReportGeneration();
        }
      },
    },
    'filters.caregiverId': {
      handler() {
        if (this.filters.dateRange && this.filters.dateRange.length === 2) {
          this.pagination.currentPage = 1;
          this.dispatchReportGeneration();
        }
      },
    },
    'filters.clientId': {
      handler() {
        if (this.filters.dateRange && this.filters.dateRange.length === 2) {
          this.pagination.currentPage = 1;
          this.dispatchReportGeneration();
        }
      },
    },
    'filters.clientAdminId': {
      handler() {
        if (this.filters.dateRange && this.filters.dateRange.length === 2) {
          this.pagination.currentPage = 1;
          this.dispatchReportGeneration();
        }
      },
    },
    'filters.caregiverAdminId': {
      handler() {
        if (this.filters.dateRange && this.filters.dateRange.length === 2) {
          this.pagination.currentPage = 1;
          this.dispatchReportGeneration();
        }
      },
    },
    'filters.serviceCodeIds': {
      handler() {
        if (this.filters.dateRange && this.filters.dateRange.length === 2) {
          this.pagination.currentPage = 1;
          this.dispatchReportGeneration();
        }
      },
    },
    'filters.payers': {
      handler() {
        if (this.filters.dateRange && this.filters.dateRange.length === 2) {
          this.pagination.currentPage = 1;
          this.dispatchReportGeneration();
        }
      },
    },
    quarterlyClientId() {
      this.quarterlyServiceCodeOptions = [];
      this.quarterlySelectedServiceCodes = [];
    },
    selectedQuarter() {
      this.quarterlyServiceCodeOptions = [];
      this.quarterlySelectedServiceCodes = [];
    },
    submissionSelectedQuarter() {
      if (this.isQuarterlySubmission && this.submissionSelectedQuarter) {
        this.pagination.currentPage = 1;
        this.generateQuarterlySubmissionReport();
      }
    },
    'submissionFilters.clientId': {
      handler() {
        if (this.isQuarterlySubmission && this.submissionSelectedQuarter) {
          this.pagination.currentPage = 1;
          this.generateQuarterlySubmissionReport();
        }
      },
    },
    'submissionFilters.payerType': {
      handler() {
        if (this.isQuarterlySubmission && this.submissionSelectedQuarter) {
          this.pagination.currentPage = 1;
          this.generateQuarterlySubmissionReport();
        }
      },
    },
    'submissionFilters.supportCoordinatorId': {
      handler() {
        if (this.isQuarterlySubmission && this.submissionSelectedQuarter) {
          this.pagination.currentPage = 1;
          this.generateQuarterlySubmissionReport();
        }
      },
    },
    'submissionFilters.status': {
      handler() {
        if (this.isQuarterlySubmission && this.submissionSelectedQuarter) {
          this.pagination.currentPage = 1;
          this.generateQuarterlySubmissionReport();
        }
      },
    },
  },
  methods: {
    async loadFilterOptions() {
      try {
        // Load offices
        const offices = await officesService.list();
        this.officeOptions = (offices || []).map(office => ({
          id: office.id,
          name: office.name,
        }));

        // Load caregivers
        const caregivers = await caregiversService.list();
        this.caregiverOptions = (caregivers || []).map(caregiver => ({
          id: caregiver.id,
          name: `${caregiver.first_name} ${caregiver.last_name}`,
        }));

        // Load clients
        const clients = await clientsService.list({ slim: true });
        this.clientOptions = (clients || []).map(client => ({
          id: client.id,
          name: `${client.first_name} ${client.last_name}`,
        }));

        // Load DSPD clients
        const dspdClients = await clientsService.list({ payer_type: 'DSPD', slim: true });
        this.dspdClientOptions = (dspdClients || []).map(client => ({
          id: client.id,
          name: `${client.first_name} ${client.last_name}`,
        }));

        // Load DSPD + Private clients for quarterly submission
        const privateClients = await clientsService.list({ payer_type: 'Private', slim: true });
        this.dspdPrivateClientOptions = [
          ...this.dspdClientOptions,
          ...(privateClients || []).map(client => ({ id: client.id, name: `${client.first_name} ${client.last_name}` })),
        ].sort((a, b) => a.name.localeCompare(b.name));

        // Load admins (caregivers with admin role)
        const admins = await caregiversService.getAdmins();
        this.adminOptions = (admins || []).map(admin => ({
          id: admin.id,
          name: admin.name || `${admin.first_name} ${admin.last_name}`,
        }));

        // Load support coordinators (admins) for quarterly submission
        const adminUsers = await adminUsersService.list();
        this.supportCoordinatorOptions = (adminUsers || [])
          .map(u => ({ id: u.id, name: `${u.first_name} ${u.last_name}` }))
          .sort((a, b) => a.name.localeCompare(b.name));

        // Load service codes
        const serviceCodes = await reportsService.getAllCodes();
        this.serviceCodeOptions = (serviceCodes || []).map(code => ({
          id: code.id,
          name: code.code,
        }));
      } catch (error) {
        this.$notifyError('Failed to load filter options');
      }
    },

    async generateReport() {
      if (!this.filters.dateRange || this.filters.dateRange.length !== 2) {
        return;
      }

      this.loadingReport = true;

      try {
        const params = {
          startDate: this.filters.dateRange[0],
          endDate: this.filters.dateRange[1],
          officeId: this.filters.officeId.length > 0 ? this.filters.officeId.join(',') : null,
          caregiverId: this.filters.caregiverId.length > 0 ? this.filters.caregiverId.join(',') : null,
          clientId: this.filters.clientId.length > 0 ? this.filters.clientId.join(',') : null,
          clientAdminId: this.filters.clientAdminId.length > 0 ? this.filters.clientAdminId.join(',') : null,
          caregiverAdminId: this.filters.caregiverAdminId.length > 0 ? this.filters.caregiverAdminId.join(',') : null,
          serviceCodeIds: this.filters.serviceCodeIds.length > 0 ? this.filters.serviceCodeIds.join(',') : null,
          payers: this.filters.payers.length > 0 ? this.filters.payers.join(',') : null,
          grouped: 'true',
        };

        const response = await reportsService.getMonthlyBillingReport(params);

        if (response.success && response.data) {
          this.reportData = response.data;
          this.pagination.total = response.data.length;
          this.pagination.totalPages = 1;
          if (this.reportData.length === 0) {
            this.$notifyError('No data found for the selected filters');
          }
        } else {
          this.reportData = [];
          this.$notifyError('No data found for the selected filters');
        }
      } catch (error) {
        this.$notifyError('Failed to generate report');
      } finally {
        this.loadingReport = false;
      }
    },

    async generateMonthlyBillingSimpleReport() {
      if (!this.filters.dateRange || this.filters.dateRange.length !== 2) {
        return;
      }

      this.loadingReport = true;

      try {
        const params = {
          startDate: this.filters.dateRange[0],
          endDate: this.filters.dateRange[1],
          officeId: this.filters.officeId.length > 0 ? this.filters.officeId.join(',') : null,
          caregiverId: this.filters.caregiverId.length > 0 ? this.filters.caregiverId.join(',') : null,
          clientId: this.filters.clientId.length > 0 ? this.filters.clientId.join(',') : null,
          clientAdminId: this.filters.clientAdminId.length > 0 ? this.filters.clientAdminId.join(',') : null,
          caregiverAdminId: this.filters.caregiverAdminId.length > 0 ? this.filters.caregiverAdminId.join(',') : null,
          serviceCodeIds: this.filters.serviceCodeIds.length > 0 ? this.filters.serviceCodeIds.join(',') : null,
          page: this.pagination.currentPage,
          pageSize: this.pagination.perPage,
          sortField: this.sortProp,
          sortOrder: this.sortOrder,
        };

        const response = await reportsService.getMonthlyBillingReportSimple(params);

        if (response.success && response.data) {
          this.reportData = response.data;
          this.pagination.total = response.total;
          this.pagination.totalPages = response.totalPages;
          if (this.reportData.length === 0) {
            this.$notifyError('No data found for the selected filters');
          }
        } else {
          this.reportData = [];
          this.$notifyError('No data found for the selected filters');
        }
      } catch (error) {
        this.$notifyError('Failed to generate report');
      } finally {
        this.loadingReport = false;
      }
    },

    async downloadCSV() {
      if (this.selectedReportType === 'timesheet-billing') {
        return this.downloadTimesheetBillingCSV();
      }
      if (this.selectedReportType === 'monthly-billing-simple') {
        return this.downloadMonthlyBillingSimpleCSV();
      }
      if (this.selectedReportType === 'dspd-shift-notes') {
        return this.downloadDspdShiftNotesCSV();
      }
      if (this.selectedReportType === 'active-auths-no-shifts') {
        return this.downloadActiveAuthsNoShiftsCSV();
      }
      if (this.selectedReportType === 'auths-with-approved-shifts') {
        return this.downloadAuthsWithApprovedShiftsCSV();
      }
      if (this.selectedReportType === 'dspd-consolidated') {
        return this.downloadDspdConsolidatedCSV();
      }
      if (this.selectedReportType === 'pba-billing') {
        return this.downloadPbaBillingCSV();
      }
      if (this.selectedReportType === 'hours-units-consolidated') {
        return this.downloadHoursUnitsConsolidatedCSV();
      }
      if (this.selectedReportType === 'all-dspd') {
        return this.downloadAllDspdCSV();
      }
      if (this.selectedReportType === 'vr-auth-consolidation') {
        return this.downloadVrAuthConsolidationCSV();
      }
      if (this.selectedReportType === 'monthly-units') {
        return this.downloadMonthlyUnitsCSV();
      }
      if (this.selectedReportType === 'attendance') {
        // Attendance doesn't have a dedicated CSV endpoint, use client-side export
        return this.downloadClientSideCSV('attendance_report');
      }
      if (this.selectedReportType === 'projection-vs-actual') {
        return this.downloadProjectionVsActualCSV();
      }
      if (this.selectedReportType === 'mileage') {
        return this.downloadMileageCSV();
      }
      if (this.selectedReportType === 'evv') {
        return this.downloadEVVCSV();
      }
      if (this.selectedReportType === 'client-compliance') {
        return this.downloadClientComplianceCSV();
      }
      if (this.selectedReportType === 'quarterly-submission') {
        return this.downloadQuarterlySubmissionCSV();
      }

      if (!this.filters.dateRange || this.filters.dateRange.length !== 2) {
        this.$notifyError('Please select a date range');
        return;
      }

      this.loadingDownload = true;

      try {
        const params = {
          startDate: this.filters.dateRange[0],
          endDate: this.filters.dateRange[1],
          officeId: this.filters.officeId.length > 0 ? this.filters.officeId.join(',') : null,
          caregiverId: this.filters.caregiverId.length > 0 ? this.filters.caregiverId.join(',') : null,
          clientId: this.filters.clientId.length > 0 ? this.filters.clientId.join(',') : null,
          clientAdminId: this.filters.clientAdminId.length > 0 ? this.filters.clientAdminId.join(',') : null,
          caregiverAdminId: this.filters.caregiverAdminId.length > 0 ? this.filters.caregiverAdminId.join(',') : null,
          serviceCodeIds: this.filters.serviceCodeIds.length > 0 ? this.filters.serviceCodeIds.join(',') : null,
          payers: this.filters.payers.length > 0 ? this.filters.payers.join(',') : null,
        };

        const blob = await reportsService.downloadMonthlyBillingReport(params);

        // Create URL from the blob
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;

        // Generate filename with date range
        const filename = `monthly_billing_report_${params.startDate}_to_${params.endDate}.csv`;
        link.setAttribute('download', filename);

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        this.$notifySuccess('Report downloaded successfully');
      } catch (error) {
        this.$notifyError('Failed to download report');
      } finally {
        this.loadingDownload = false;
      }
    },

    async downloadMonthlyBillingSimpleCSV() {
      if (!this.filters.dateRange || this.filters.dateRange.length !== 2) {
        this.$notifyError('Please select a date range');
        return;
      }

      this.loadingDownload = true;

      try {
        const params = {
          startDate: this.filters.dateRange[0],
          endDate: this.filters.dateRange[1],
          officeId: this.filters.officeId.length > 0 ? this.filters.officeId.join(',') : null,
          caregiverId: this.filters.caregiverId.length > 0 ? this.filters.caregiverId.join(',') : null,
          clientId: this.filters.clientId.length > 0 ? this.filters.clientId.join(',') : null,
          clientAdminId: this.filters.clientAdminId.length > 0 ? this.filters.clientAdminId.join(',') : null,
          caregiverAdminId: this.filters.caregiverAdminId.length > 0 ? this.filters.caregiverAdminId.join(',') : null,
          serviceCodeIds: this.filters.serviceCodeIds.length > 0 ? this.filters.serviceCodeIds.join(',') : null,
        };

        const blob = await reportsService.downloadMonthlyBillingReportSimple(params);

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `monthly_billing_report_${params.startDate}_to_${params.endDate}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        this.$notifySuccess('Report downloaded successfully');
      } catch (error) {
        this.$notifyError('Failed to download report');
      } finally {
        this.loadingDownload = false;
      }
    },

    async generateActiveAuthsNoShiftsReport() {
      if (!this.filters.dateRange || this.filters.dateRange.length !== 2) {
        return;
      }

      this.loadingReport = true;

      try {
        const params = {
          startDate: this.filters.dateRange[0],
          endDate: this.filters.dateRange[1],
          officeId: this.filters.officeId.length > 0 ? this.filters.officeId.join(',') : null,
          clientId: this.filters.clientId.length > 0 ? this.filters.clientId.join(',') : null,
          clientAdminId: this.filters.clientAdminId.length > 0 ? this.filters.clientAdminId.join(',') : null,
          serviceCodeIds: this.filters.serviceCodeIds.length > 0 ? this.filters.serviceCodeIds.join(',') : null,
          page: this.pagination.currentPage,
          pageSize: this.pagination.perPage,
          sortField: this.sortProp,
          sortOrder: this.sortOrder,
        };

        const response = await reportsService.getActiveAuthsNoShiftsReport(params);

        if (response.success && response.data) {
          this.reportData = response.data;
          this.pagination.total = response.total || response.data.length;
          this.pagination.totalPages = response.totalPages || Math.ceil(response.total / this.pagination.perPage);
          if (this.reportData.length === 0) {
            this.$notifyError('No unserved active clients found');
          }
        } else {
          this.reportData = [];
          this.$notifyError('No unserved active clients found');
        }
      } catch (error) {
        this.$notifyError('Failed to generate report');
      } finally {
        this.loadingReport = false;
      }
    },

    async downloadActiveAuthsNoShiftsCSV() {
      if (!this.filters.dateRange || this.filters.dateRange.length !== 2) {
        this.$notifyError('Please select a date range');
        return;
      }

      this.loadingDownload = true;

      try {
        const params = {
          startDate: this.filters.dateRange[0],
          endDate: this.filters.dateRange[1],
          officeId: this.filters.officeId.length > 0 ? this.filters.officeId.join(',') : null,
          clientId: this.filters.clientId.length > 0 ? this.filters.clientId.join(',') : null,
          clientAdminId: this.filters.clientAdminId.length > 0 ? this.filters.clientAdminId.join(',') : null,
          serviceCodeIds: this.filters.serviceCodeIds.length > 0 ? this.filters.serviceCodeIds.join(',') : null,
        };

        const blob = await reportsService.downloadActiveAuthsNoShiftsReport(params);

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;

        const filename = `unserved_active_clients_report_${params.startDate}_to_${params.endDate}.csv`;
        link.setAttribute('download', filename);

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        this.$notifySuccess('Report downloaded successfully');
      } catch (error) {
        this.$notifyError('Failed to download report');
      } finally {
        this.loadingDownload = false;
      }
    },

    async generateAuthsWithApprovedShiftsReport() {
      if (!this.filters.dateRange || this.filters.dateRange.length !== 2) {
        return;
      }

      this.loadingReport = true;

      try {
        const params = {
          startDate: this.filters.dateRange[0],
          endDate: this.filters.dateRange[1],
          officeId: this.filters.officeId.length > 0 ? this.filters.officeId.join(',') : null,
          clientId: this.filters.clientId.length > 0 ? this.filters.clientId.join(',') : null,
          clientAdminId: this.filters.clientAdminId.length > 0 ? this.filters.clientAdminId.join(',') : null,
          serviceCodeIds: this.filters.serviceCodeIds.length > 0 ? this.filters.serviceCodeIds.join(',') : null,
          page: this.pagination.currentPage,
          pageSize: this.pagination.perPage,
          sortField: this.sortProp,
          sortOrder: this.sortOrder,
        };

        const response = await reportsService.getAuthsWithApprovedShiftsReport(params);

        if (response.success && response.data) {
          this.reportData = response.data;
          this.pagination.total = response.total || response.data.length;
          this.pagination.totalPages = response.totalPages || Math.ceil(response.total / this.pagination.perPage);
          if (this.reportData.length === 0) {
            this.$notifyError('No authorizations with approved shifts found for the selected date range');
          }
        } else {
          this.reportData = [];
          this.$notifyError('No authorizations with approved shifts found for the selected date range');
        }
      } catch (error) {
        this.$notifyError('Failed to generate report');
      } finally {
        this.loadingReport = false;
      }
    },

    async downloadAuthsWithApprovedShiftsCSV() {
      if (!this.filters.dateRange || this.filters.dateRange.length !== 2) {
        this.$notifyError('Please select a date range');
        return;
      }

      this.loadingDownload = true;

      try {
        const params = {
          startDate: this.filters.dateRange[0],
          endDate: this.filters.dateRange[1],
          officeId: this.filters.officeId.length > 0 ? this.filters.officeId.join(',') : null,
          clientId: this.filters.clientId.length > 0 ? this.filters.clientId.join(',') : null,
          clientAdminId: this.filters.clientAdminId.length > 0 ? this.filters.clientAdminId.join(',') : null,
          serviceCodeIds: this.filters.serviceCodeIds.length > 0 ? this.filters.serviceCodeIds.join(',') : null,
        };

        const blob = await reportsService.downloadAuthsWithApprovedShiftsReport(params);

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;

        const filename = `auths_with_approved_shifts_report_${params.startDate}_to_${params.endDate}.csv`;
        link.setAttribute('download', filename);

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        this.$notifySuccess('Report downloaded successfully');
      } catch (error) {
        this.$notifyError('Failed to download report');
      } finally {
        this.loadingDownload = false;
      }
    },

    async downloadDspdShiftNotesCSV() {
      if (!this.filters.dateRange || this.filters.dateRange.length !== 2) {
        this.$notifyError('Please select a date range');
        return;
      }

      this.loadingDownload = true;

      try {
        const params = {
          startDate: this.filters.dateRange[0],
          endDate: this.filters.dateRange[1],
          clientId: this.filters.clientId.length > 0 ? this.filters.clientId.join(',') : null,
        };

        const blob = await reportsService.downloadDspdShiftNotesReport(params);

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;

        const filename = `dspd_shift_notes_report_${params.startDate}_to_${params.endDate}.csv`;
        link.setAttribute('download', filename);

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        this.$notifySuccess('Report downloaded successfully');
      } catch (error) {
        this.$notifyError('Failed to download report');
      } finally {
        this.loadingDownload = false;
      }
    },

    async generateDspdShiftNotesReport() {
      if (!this.filters.dateRange || this.filters.dateRange.length !== 2) return;
      this.loadingReport = true;
      try {
        const params = {
          startDate: this.filters.dateRange[0],
          endDate:   this.filters.dateRange[1],
          clientId:  this.filters.clientId.length > 0 ? this.filters.clientId.join(',') : null,
          page:      this.pagination.currentPage,
          pageSize:  this.pagination.perPage,
          sortField: this.sortProp,
          sortOrder: this.sortOrder,
        };
        const response = await reportsService.getDspdShiftNotesReport(params);
        if (response.success && response.data) {
          this.reportData = response.data;
          this.pagination.total = response.total || response.data.length;
          this.pagination.totalPages = response.totalPages || Math.ceil(response.total / this.pagination.perPage);
          if (this.reportData.length === 0) this.$notifyError('No DSPD shift notes found for the selected filters');
        } else {
          this.reportData = [];
          this.$notifyError('No DSPD shift notes found');
        }
      } catch (error) {
        this.$notifyError('Failed to generate report');
      } finally {
        this.loadingReport = false;
      }
    },

    handleSortChange({ prop, order }) {
      this.sortProp = prop;
      this.sortOrder = order;
      this.pagination.currentPage = 1;
      this.dispatchReportGeneration();
    },

    handlePageChange(page) {
      this.pagination.currentPage = page;
      this.dispatchReportGeneration();
    },

    handlePageSizeChange(pageSize) {
      this.pagination.perPage = pageSize;
      this.pagination.currentPage = 1;
      this.dispatchReportGeneration();
    },

    dispatchReportGeneration() {
      switch (this.selectedReportType) {
        case 'active-auths-no-shifts':
          return this.generateActiveAuthsNoShiftsReport();
        case 'auths-with-approved-shifts':
          return this.generateAuthsWithApprovedShiftsReport();
        case 'dspd-shift-notes':
          return this.generateDspdShiftNotesReport();
        case 'dspd-consolidated':
          return this.generateDspdConsolidatedReport();
        case 'pba-billing':
          return this.generatePbaBillingReport();
        case 'hours-units-consolidated':
          return this.generateHoursUnitsConsolidatedReport();
        case 'all-dspd':
          return this.generateAllDspdReport();
        case 'vr-auth-consolidation':
          return this.generateVrAuthConsolidationReport();
        case 'monthly-units':
          return this.generateMonthlyUnitsReport();
        case 'attendance':
          return this.generateAttendanceReport();
        case 'projection-vs-actual':
          return this.generateProjectionVsActualReport();
        case 'mileage':
          return this.generateMileageReport();
        case 'evv':
          return this.generateEVVReport();
        case 'timesheet-billing':
          return this.generateTimesheetBillingReport();
        case 'monthly-billing-simple':
          return this.generateMonthlyBillingSimpleReport();
        case 'client-compliance':
          return this.generateClientComplianceReport();
        case 'quarterly-submission':
          return this.generateQuarterlySubmissionReport();
        default:
          return this.generateReport();
      }
    },

    async generateDspdConsolidatedReport() {
      if (!this.filters.dateRange || this.filters.dateRange.length !== 2) return;
      this.loadingReport = true;
      try {
        const params = {
          startDate: this.filters.dateRange[0],
          endDate: this.filters.dateRange[1],
          clientId: this.filters.clientId.length > 0 ? this.filters.clientId.join(',') : null,
          page: this.pagination.currentPage,
          pageSize: this.pagination.perPage,
          sortField: this.sortProp,
          sortOrder: this.sortOrder,
        };
        const response = await reportsService.getDspdConsolidatedBillingReport(params);
        if (response.success && response.data) {
          this.reportData = response.data;
          this.pagination.total = response.total || response.data.length;
          this.pagination.totalPages = response.totalPages || Math.ceil(response.total / this.pagination.perPage);
          if (this.reportData.length === 0) this.$notifyError('No data found for the selected filters');
        } else {
          this.reportData = [];
          this.$notifyError('No data found for the selected filters');
        }
      } catch (error) {
        this.$notifyError('Failed to generate report');
      } finally {
        this.loadingReport = false;
      }
    },

    async downloadDspdConsolidatedCSV() {
      if (!this.filters.dateRange || this.filters.dateRange.length !== 2) {
        this.$notifyError('Please select a date range');
        return;
      }
      this.loadingDownload = true;
      try {
        const params = {
          startDate: this.filters.dateRange[0],
          endDate: this.filters.dateRange[1],
          clientId: this.filters.clientId.length > 0 ? this.filters.clientId.join(',') : null,
        };
        const blob = await reportsService.downloadDspdConsolidatedBillingReport(params);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `dspd_consolidated_billing_${params.startDate}_to_${params.endDate}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        this.$notifySuccess('Report downloaded successfully');
      } catch (error) {
        this.$notifyError('Failed to download report');
      } finally {
        this.loadingDownload = false;
      }
    },

    async generatePbaBillingReport() {
      if (!this.filters.dateRange || this.filters.dateRange.length !== 2) return;
      this.loadingReport = true;
      try {
        const params = {
          startDate: this.filters.dateRange[0],
          endDate: this.filters.dateRange[1],
          clientId: this.filters.clientId.length > 0 ? this.filters.clientId.join(',') : null,
          page: this.pagination.currentPage,
          pageSize: this.pagination.perPage,
          sortField: this.sortProp,
          sortOrder: this.sortOrder,
        };
        const response = await reportsService.getPbaBillingReport(params);
        if (response.success && response.data) {
          this.reportData = response.data;
          this.pagination.total = response.total || response.data.length;
          this.pagination.totalPages = response.totalPages || Math.ceil(response.total / this.pagination.perPage);
          if (this.reportData.length === 0) this.$notifyError('No data found for the selected filters');
        } else {
          this.reportData = [];
          this.$notifyError('No data found for the selected filters');
        }
      } catch (error) {
        this.$notifyError('Failed to generate report');
      } finally {
        this.loadingReport = false;
      }
    },

    async downloadPbaBillingCSV() {
      if (!this.filters.dateRange || this.filters.dateRange.length !== 2) {
        this.$notifyError('Please select a date range');
        return;
      }
      this.loadingDownload = true;
      try {
        const params = {
          startDate: this.filters.dateRange[0],
          endDate: this.filters.dateRange[1],
          clientId: this.filters.clientId.length > 0 ? this.filters.clientId.join(',') : null,
        };
        const blob = await reportsService.downloadPbaBillingReport(params);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `pba_billing_report_${params.startDate}_to_${params.endDate}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        this.$notifySuccess('Report downloaded successfully');
      } catch (error) {
        this.$notifyError('Failed to download report');
      } finally {
        this.loadingDownload = false;
      }
    },

    async generateHoursUnitsConsolidatedReport() {
      if (!this.filters.dateRange || this.filters.dateRange.length !== 2) return;
      this.loadingReport = true;
      try {
        const params = {
          startDate: this.filters.dateRange[0],
          endDate: this.filters.dateRange[1],
          clientId: this.filters.clientId.length > 0 ? this.filters.clientId.join(',') : null,
          page: this.pagination.currentPage,
          pageSize: this.pagination.perPage,
          sortField: this.sortProp,
          sortOrder: this.sortOrder,
        };
        const response = await reportsService.getHoursUnitsConsolidatedReport(params);
        if (response.success && response.data) {
          this.reportData = response.data;
          this.pagination.total = response.total || response.data.length;
          this.pagination.totalPages = response.totalPages || Math.ceil(response.total / this.pagination.perPage);
          if (this.reportData.length === 0) this.$notifyError('No data found for the selected filters');
        } else {
          this.reportData = [];
          this.$notifyError('No data found for the selected filters');
        }
      } catch (error) {
        this.$notifyError('Failed to generate report');
      } finally {
        this.loadingReport = false;
      }
    },

    async downloadHoursUnitsConsolidatedCSV() {
      if (!this.filters.dateRange || this.filters.dateRange.length !== 2) {
        this.$notifyError('Please select a date range');
        return;
      }
      this.loadingDownload = true;
      try {
        const params = {
          startDate: this.filters.dateRange[0],
          endDate: this.filters.dateRange[1],
          clientId: this.filters.clientId.length > 0 ? this.filters.clientId.join(',') : null,
        };
        const blob = await reportsService.downloadHoursUnitsConsolidatedReport(params);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `hours_units_consolidated_${params.startDate}_to_${params.endDate}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        this.$notifySuccess('Report downloaded successfully');
      } catch (error) {
        this.$notifyError('Failed to download report');
      } finally {
        this.loadingDownload = false;
      }
    },

    async generateAllDspdReport() {
      if (!this.filters.dateRange || this.filters.dateRange.length !== 2) return;
      this.loadingReport = true;
      try {
        const params = {
          startDate: this.filters.dateRange[0],
          endDate: this.filters.dateRange[1],
          clientId: this.filters.clientId.length > 0 ? this.filters.clientId.join(',') : null,
          page: this.pagination.currentPage,
          pageSize: this.pagination.perPage,
          sortField: this.sortProp,
          sortOrder: this.sortOrder,
        };
        const response = await reportsService.getAllDspdReport(params);
        if (response.success && response.data) {
          this.reportData = response.data;
          this.pagination.total = response.total || response.data.length;
          this.pagination.totalPages = response.totalPages || Math.ceil(response.total / this.pagination.perPage);
          if (this.reportData.length === 0) this.$notifyError('No data found for the selected filters');
        } else {
          this.reportData = [];
          this.$notifyError('No data found for the selected filters');
        }
      } catch (error) {
        this.$notifyError('Failed to generate report');
      } finally {
        this.loadingReport = false;
      }
    },

    async downloadAllDspdCSV() {
      if (!this.filters.dateRange || this.filters.dateRange.length !== 2) {
        this.$notifyError('Please select a date range');
        return;
      }
      this.loadingDownload = true;
      try {
        const params = {
          startDate: this.filters.dateRange[0],
          endDate: this.filters.dateRange[1],
          clientId: this.filters.clientId.length > 0 ? this.filters.clientId.join(',') : null,
        };
        const blob = await reportsService.downloadAllDspdReport(params);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `all_dspd_billing_${params.startDate}_to_${params.endDate}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        this.$notifySuccess('Report downloaded successfully');
      } catch (error) {
        this.$notifyError('Failed to download report');
      } finally {
        this.loadingDownload = false;
      }
    },

    async generateVrAuthConsolidationReport() {
      if (!this.filters.dateRange || this.filters.dateRange.length !== 2) return;
      this.loadingReport = true;
      try {
        const params = {
          startDate: this.filters.dateRange[0],
          endDate: this.filters.dateRange[1],
          clientId: this.filters.clientId.length > 0 ? this.filters.clientId.join(',') : null,
          page: this.pagination.currentPage,
          pageSize: this.pagination.perPage,
          sortField: this.sortProp,
          sortOrder: this.sortOrder,
        };
        const response = await reportsService.getVrAuthConsolidationReport(params);
        if (response.success && response.data) {
          this.reportData = response.data;
          this.pagination.total = response.total || response.data.length;
          this.pagination.totalPages = response.totalPages || Math.ceil(response.total / this.pagination.perPage);
          if (this.reportData.length === 0) this.$notifyError('No data found for the selected filters');
        } else {
          this.reportData = [];
          this.$notifyError('No data found for the selected filters');
        }
      } catch (error) {
        this.$notifyError('Failed to generate report');
      } finally {
        this.loadingReport = false;
      }
    },

    async downloadVrAuthConsolidationCSV() {
      if (!this.filters.dateRange || this.filters.dateRange.length !== 2) {
        this.$notifyError('Please select a date range');
        return;
      }
      this.loadingDownload = true;
      try {
        const params = {
          startDate: this.filters.dateRange[0],
          endDate: this.filters.dateRange[1],
          clientId: this.filters.clientId.length > 0 ? this.filters.clientId.join(',') : null,
        };
        const blob = await reportsService.downloadVrAuthConsolidationReport(params);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `vr_auth_consolidation_${params.startDate}_to_${params.endDate}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        this.$notifySuccess('Report downloaded successfully');
      } catch (error) {
        this.$notifyError('Failed to download report');
      } finally {
        this.loadingDownload = false;
      }
    },

    handleDateRangeTypeChange(value) {
      this.dateRangeType = value;
      if (value === 'custom') {
        // Clear filters.dateRange to force user to select
        this.filters.dateRange = null;
      } else {
        // Initialize selectedDate to today if not set
        if (!this.selectedDate) {
          const today = new Date();
          const year = today.getFullYear();
          const month = String(today.getMonth() + 1).padStart(2, '0');
          const day = String(today.getDate()).padStart(2, '0');
          this.selectedDate = `${year}-${month}-${day}`;
        } else {
          // Update the date range based on the selected date
          this.updateDateRangeFromSelectedDate();
          this.$nextTick(() => {
            this.updateDatePickerDisplay();
          });
        }
      }
    },

    updateDatePickerDisplay() {
      this.$nextTick(() => {
        try {
          const input = this.$refs.datePicker?.$el?.querySelector("input");
          if (input && this.dateRangeType !== 'custom' && this.dateRangeDisplay) {
            input.value = this.dateRangeDisplay;
          }
        } catch (error) {
          // Silently fail if date picker is not available
        }
      });
    },

    updateDateRangeFromSelectedDate() {
      if (!this.selectedDate) return;

      let dateObj = this.selectedDate;
      if (typeof dateObj === 'string') {
        const [year, month, day] = dateObj.split('-').map(Number);
        dateObj = new Date(year, month - 1, day);
      } else if (!(dateObj instanceof Date)) {
        dateObj = new Date(dateObj);
      }

      let startDate, endDate;

      switch (this.dateRangeType) {
        case 'day':
          startDate = new Date(dateObj);
          endDate = new Date(dateObj);
          break;

        case 'week':
          // Calculate Sunday to Saturday
          const dayOfWeek = dateObj.getDay();
          startDate = new Date(dateObj);
          startDate.setDate(dateObj.getDate() - dayOfWeek);
          endDate = new Date(startDate);
          endDate.setDate(startDate.getDate() + 6);
          break;

        case 'two-weeks':
          // Calculate two weeks starting from Sunday
          const dayOfWeek2 = dateObj.getDay();
          startDate = new Date(dateObj);
          startDate.setDate(dateObj.getDate() - dayOfWeek2);
          endDate = new Date(startDate);
          endDate.setDate(startDate.getDate() + 13); // 14 days total (0-13)
          break;

        case 'month':
          startDate = new Date(dateObj.getFullYear(), dateObj.getMonth(), 1);
          endDate = new Date(dateObj.getFullYear(), dateObj.getMonth() + 1, 0);
          break;

        default:
          return;
      }

      const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      this.filters.dateRange = [formatDate(startDate), formatDate(endDate)];
    },

    goToPrevious() {
      if (!this.selectedDate) return;

      let dateObj = this.selectedDate;
      if (typeof dateObj === 'string') {
        const [year, month, day] = dateObj.split('-').map(Number);
        dateObj = new Date(year, month - 1, day);
      } else if (!(dateObj instanceof Date)) {
        dateObj = new Date(dateObj);
      }

      const newDate = new Date(dateObj);

      switch (this.dateRangeType) {
        case 'day':
          newDate.setDate(newDate.getDate() - 1);
          break;
        case 'week':
          newDate.setDate(newDate.getDate() - 7);
          break;
        case 'two-weeks':
          newDate.setDate(newDate.getDate() - 14);
          break;
        case 'month':
          newDate.setMonth(newDate.getMonth() - 1);
          break;
      }

      const year = newDate.getFullYear();
      const month = String(newDate.getMonth() + 1).padStart(2, '0');
      const day = String(newDate.getDate()).padStart(2, '0');
      this.selectedDate = `${year}-${month}-${day}`;

      this.$nextTick(() => {
        this.updateDatePickerDisplay();
      });
    },

    goToNext() {
      if (!this.selectedDate) return;

      let dateObj = this.selectedDate;
      if (typeof dateObj === 'string') {
        const [year, month, day] = dateObj.split('-').map(Number);
        dateObj = new Date(year, month - 1, day);
      } else if (!(dateObj instanceof Date)) {
        dateObj = new Date(dateObj);
      }

      const newDate = new Date(dateObj);

      switch (this.dateRangeType) {
        case 'day':
          newDate.setDate(newDate.getDate() + 1);
          break;
        case 'week':
          newDate.setDate(newDate.getDate() + 7);
          break;
        case 'two-weeks':
          newDate.setDate(newDate.getDate() + 14);
          break;
        case 'month':
          newDate.setMonth(newDate.getMonth() + 1);
          break;
      }

      const year = newDate.getFullYear();
      const month = String(newDate.getMonth() + 1).padStart(2, '0');
      const day = String(newDate.getDate()).padStart(2, '0');
      this.selectedDate = `${year}-${month}-${day}`;

      this.$nextTick(() => {
        this.updateDatePickerDisplay();
      });
    },
    openResponsesModal(row) {
      this.selectedResponsesRow = row;
      this.showResponsesModal = true;
    },

    getCaregiverTooltipContent(row) {
      if (!row.caregiver_names || row.caregiver_names.length === 0) return '';
      return row.caregiver_names.join('<br>');
    },

    formatTableDate(dateStr) {
      if (!dateStr) return "";
      const [year, month, day] = dateStr.split("-").map(Number);
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
      });
    },

    formatEVVTime(timeStr) {
      if (!timeStr) return "";
      const [h, m] = timeStr.split(":").map(Number);
      if (Number.isNaN(h) || Number.isNaN(m)) return timeStr;
      const hour12 = ((h + 11) % 12) + 1;
      const suffix = h < 12 ? "am" : "pm";
      return `${hour12}:${String(m).padStart(2, "0")} ${suffix}`;
    },

    formatEVVGeocode(lat, lng) {
      if (lat === "" || lat == null || lng === "" || lng == null) return "";
      const latNum = Number(lat);
      const lngNum = Number(lng);
      if (Number.isNaN(latNum) || Number.isNaN(lngNum)) return "";
      return `${latNum.toFixed(3)}, ${lngNum.toFixed(3)}`;
    },

    async generateMonthlyUnitsReport() {
      if (!this.filters.dateRange || this.filters.dateRange.length !== 2) return;
      this.loadingReport = true;
      try {
        const params = {
          startDate: this.filters.dateRange[0],
          endDate: this.filters.dateRange[1],
          clientId: this.filters.clientId.length > 0 ? this.filters.clientId.join(',') : null,
          officeId: this.filters.officeId.length > 0 ? this.filters.officeId.join(',') : null,
          page: this.pagination.currentPage,
          pageSize: this.pagination.perPage,
          sortField: this.sortProp,
          sortOrder: this.sortOrder,
        };
        const response = await reportsService.getMonthlyUnitsReport(params);
        if (response.success && response.data) {
          this.reportData = response.data;
          this.pagination.total = response.total || response.data.length;
          this.pagination.totalPages = response.totalPages || Math.ceil(response.total / this.pagination.perPage);
          if (this.reportData.length === 0) this.$notifyError('No data found for the selected filters');
        } else {
          this.reportData = [];
          this.$notifyError('No data found for the selected filters');
        }
      } catch {
        this.$notifyError('Failed to generate monthly units report');
      } finally {
        this.loadingReport = false;
      }
    },

    async downloadMonthlyUnitsCSV() {
      if (!this.filters.dateRange || this.filters.dateRange.length !== 2) {
        this.$notifyError('Please select a date range');
        return;
      }
      this.loadingDownload = true;
      try {
        const params = {
          startDate: this.filters.dateRange[0],
          endDate: this.filters.dateRange[1],
          clientId: this.filters.clientId.length > 0 ? this.filters.clientId.join(',') : null,
          officeId: this.filters.officeId.length > 0 ? this.filters.officeId.join(',') : null,
        };
        const blob = await reportsService.downloadMonthlyUnitsCSV(params);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `monthly_units_${params.startDate}_to_${params.endDate}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        this.$notifySuccess('Report downloaded successfully');
      } catch {
        this.$notifyError('Failed to download report');
      } finally {
        this.loadingDownload = false;
      }
    },

    async generateAttendanceReport() {
      if (!this.filters.dateRange || this.filters.dateRange.length !== 2) return;
      this.loadingReport = true;
      try {
        const params = {
          startDate: this.filters.dateRange[0],
          endDate: this.filters.dateRange[1],
          clientId: this.filters.clientId.length > 0 ? this.filters.clientId.join(',') : null,
          page: this.pagination.currentPage,
          pageSize: this.pagination.perPage,
          sortField: this.sortProp,
          sortOrder: this.sortOrder,
        };
        const response = await reportsService.getAttendanceReport(params);
        if (response.success && response.data) {
          this.reportData = response.data;
          this.pagination.total = response.total || response.data.length;
          this.pagination.totalPages = response.totalPages || Math.ceil(response.total / this.pagination.perPage);
          if (this.reportData.length === 0) this.$notifyError('No attendance data found');
        } else {
          this.reportData = [];
          this.$notifyError('No attendance data found');
        }
      } catch {
        this.$notifyError('Failed to generate attendance report');
      } finally {
        this.loadingReport = false;
      }
    },

    async generateProjectionVsActualReport() {
      if (!this.filters.dateRange || this.filters.dateRange.length !== 2) return;
      this.loadingReport = true;
      try {
        const params = {
          startDate: this.filters.dateRange[0],
          endDate: this.filters.dateRange[1],
          payer: this.filters.payers.length > 0 ? this.filters.payers.join(',') : null,
          serviceCodeIds: this.filters.serviceCodeIds.length > 0 ? this.filters.serviceCodeIds.join(',') : null,
          officeId: this.filters.officeId.length > 0 ? this.filters.officeId.join(',') : null,
          clientId: this.filters.clientId.length > 0 ? this.filters.clientId.join(',') : null,
          page: this.pagination.currentPage,
          pageSize: this.pagination.perPage,
          sortField: this.sortProp,
          sortOrder: this.sortOrder,
        };
        const response = await reportsService.getProjectionVsActualReport(params);
        if (response.success && response.data) {
          this.reportData = response.data;
          this.pagination.total = response.total || response.data.length;
          this.pagination.totalPages = response.totalPages || Math.ceil(response.total / this.pagination.perPage);
          if (this.reportData.length === 0) this.$notifyError('No data found for the selected filters');
        } else {
          this.reportData = [];
          this.$notifyError('No data found for the selected filters');
        }
      } catch {
        this.$notifyError('Failed to generate projection vs actual report');
      } finally {
        this.loadingReport = false;
      }
    },

    async downloadProjectionVsActualCSV() {
      if (!this.filters.dateRange || this.filters.dateRange.length !== 2) {
        this.$notifyError('Please select a date range');
        return;
      }
      this.loadingDownload = true;
      try {
        const params = {
          startDate: this.filters.dateRange[0],
          endDate: this.filters.dateRange[1],
          payer: this.filters.payers.length > 0 ? this.filters.payers.join(',') : null,
          serviceCodeIds: this.filters.serviceCodeIds.length > 0 ? this.filters.serviceCodeIds.join(',') : null,
          officeId: this.filters.officeId.length > 0 ? this.filters.officeId.join(',') : null,
          clientId: this.filters.clientId.length > 0 ? this.filters.clientId.join(',') : null,
        };
        const blob = await reportsService.downloadProjectionVsActualReport(params);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `projection_vs_actual_${params.startDate}_to_${params.endDate}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        this.$notifySuccess('Report downloaded successfully');
      } catch {
        this.$notifyError('Failed to download report');
      } finally {
        this.loadingDownload = false;
      }
    },

    onApproverEmailChange() {
      localStorage.setItem('timesheetApproverEmail', this.timesheetApproverEmail || '');
      if (this.selectedReportType === 'timesheet-billing') {
        this.generateTimesheetBillingReport();
      }
    },

    timesheetBillingParams() {
      return {
        startDate: this.filters.dateRange[0],
        endDate: this.filters.dateRange[1],
        payers: this.filters.payers.length > 0 ? this.filters.payers.join(',') : null,
        serviceCodeIds: this.filters.serviceCodeIds.length > 0 ? this.filters.serviceCodeIds.join(',') : null,
        officeId: this.filters.officeId.length > 0 ? this.filters.officeId.join(',') : null,
        clientId: this.filters.clientId.length > 0 ? this.filters.clientId.join(',') : null,
        caregiverId: this.filters.caregiverId.length > 0 ? this.filters.caregiverId.join(',') : null,
        providerApproverEmail: this.timesheetApproverEmail || null,
      };
    },

    async generateTimesheetBillingReport() {
      if (!this.filters.dateRange || this.filters.dateRange.length !== 2) return;
      this.loadingReport = true;
      try {
        const response = await reportsService.getTimesheetBillingReport({
          ...this.timesheetBillingParams(),
          sortField: this.sortProp,
          sortOrder: this.sortOrder,
        });
        if (response.success && response.data) {
          this.reportData = response.data;
          this.timesheetWarnings = response.warnings || [];
          this.pagination.total = response.data.length;
          this.pagination.totalPages = 1;
          if (this.reportData.length === 0) this.$notifyError('No data found for the selected filters');
        } else {
          this.reportData = [];
          this.timesheetWarnings = [];
          this.$notifyError('No data found for the selected filters');
        }
      } catch {
        this.$notifyError('Failed to generate timesheet billing report');
      } finally {
        this.loadingReport = false;
      }
    },

    async downloadTimesheetBillingCSV() {
      if (!this.filters.dateRange || this.filters.dateRange.length !== 2) {
        this.$notifyError('Please select a date range');
        return;
      }
      this.loadingDownload = true;
      try {
        const params = this.timesheetBillingParams();
        const blob = await reportsService.downloadTimesheetBillingReport(params);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `timesheet_billing_report_${params.startDate}_${params.endDate}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch {
        this.$notifyError('Failed to download timesheet billing report');
      } finally {
        this.loadingDownload = false;
      }
    },

    async generateEVVReport() {
      if (!this.filters.dateRange || this.filters.dateRange.length !== 2) return;
      this.loadingReport = true;
      try {
        const params = {
          startDate: this.filters.dateRange[0],
          endDate: this.filters.dateRange[1],
          payers: this.filters.payers.length > 0 ? this.filters.payers.join(',') : null,
          serviceCodeIds: this.filters.serviceCodeIds.length > 0 ? this.filters.serviceCodeIds.join(',') : null,
          officeId: this.filters.officeId.length > 0 ? this.filters.officeId.join(',') : null,
          clientId: this.filters.clientId.length > 0 ? this.filters.clientId.join(',') : null,
          caregiverId: this.filters.caregiverId.length > 0 ? this.filters.caregiverId.join(',') : null,
          page: this.pagination.currentPage,
          pageSize: this.pagination.perPage,
          sortField: this.sortProp,
          sortOrder: this.sortOrder,
        };
        const response = await reportsService.getEVVReport(params);
        if (response.success && response.data) {
          this.reportData = response.data.map((row) => ({
            ...row,
            begin_geocode: this.formatEVVGeocode(row.begin_lat, row.begin_lng),
            end_geocode: this.formatEVVGeocode(row.end_lat, row.end_lng),
          }));
          this.pagination.total = response.total || response.data.length;
          this.pagination.totalPages = response.totalPages || Math.ceil(response.total / this.pagination.perPage);
          if (this.reportData.length === 0) this.$notifyError('No data found for the selected filters');
        } else {
          this.reportData = [];
          this.$notifyError('No data found for the selected filters');
        }
      } catch {
        this.$notifyError('Failed to generate EVV report');
      } finally {
        this.loadingReport = false;
      }
    },

    async downloadEVVCSV() {
      if (!this.filters.dateRange || this.filters.dateRange.length !== 2) {
        this.$notifyError('Please select a date range');
        return;
      }
      this.loadingDownload = true;
      try {
        const params = {
          startDate: this.filters.dateRange[0],
          endDate: this.filters.dateRange[1],
          payers: this.filters.payers.length > 0 ? this.filters.payers.join(',') : null,
          serviceCodeIds: this.filters.serviceCodeIds.length > 0 ? this.filters.serviceCodeIds.join(',') : null,
          officeId: this.filters.officeId.length > 0 ? this.filters.officeId.join(',') : null,
          clientId: this.filters.clientId.length > 0 ? this.filters.clientId.join(',') : null,
          caregiverId: this.filters.caregiverId.length > 0 ? this.filters.caregiverId.join(',') : null,
        };
        const blob = await reportsService.downloadEVVReport(params);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `evv_report_${params.startDate}_to_${params.endDate}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        this.$notifySuccess('Report downloaded successfully');
      } catch {
        this.$notifyError('Failed to download report');
      } finally {
        this.loadingDownload = false;
      }
    },

    async generateMileageReport() {
      if (!this.filters.dateRange || this.filters.dateRange.length !== 2) return;
      this.loadingReport = true;
      try {
        const params = {
          startDate: this.filters.dateRange[0],
          endDate: this.filters.dateRange[1],
          officeId: this.filters.officeId.length > 0 ? this.filters.officeId.join(',') : null,
          caregiverId: this.filters.caregiverId.length > 0 ? this.filters.caregiverId.join(',') : null,
          page: this.pagination.currentPage,
          pageSize: this.pagination.perPage,
          sortField: this.sortProp,
          sortOrder: this.sortOrder,
        };
        const response = await reportsService.getMileageReport(params);
        if (response.success && response.data) {
          this.reportData = response.data;
          this.mileageAggregations = response.aggregations || { byCaregiver: [], byManager: [] };
          this.pagination.total = response.total || response.data.length;
          this.pagination.totalPages = response.totalPages || Math.ceil(response.total / this.pagination.perPage);
          if (this.reportData.length === 0) this.$notifyError('No mileage data found for the selected filters');
        } else {
          this.reportData = [];
          this.mileageAggregations = { byCaregiver: [], byManager: [] };
          this.$notifyError('No mileage data found for the selected filters');
        }
      } catch {
        this.$notifyError('Failed to generate mileage report');
      } finally {
        this.loadingReport = false;
      }
    },

    async downloadMileageCSV() {
      if (!this.filters.dateRange || this.filters.dateRange.length !== 2) {
        this.$notifyError('Please select a date range');
        return;
      }
      this.loadingDownload = true;
      try {
        const params = {
          startDate: this.filters.dateRange[0],
          endDate: this.filters.dateRange[1],
          officeId: this.filters.officeId.length > 0 ? this.filters.officeId.join(',') : null,
          caregiverId: this.filters.caregiverId.length > 0 ? this.filters.caregiverId.join(',') : null,
        };
        const blob = await reportsService.downloadMileageReport(params);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `mileage_report_${params.startDate}_to_${params.endDate}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        this.$notifySuccess('Report downloaded successfully');
      } catch {
        this.$notifyError('Failed to download report');
      } finally {
        this.loadingDownload = false;
      }
    },

    async generateClientComplianceReport() {
      this.loadingReport = true;
      try {
        const params = {
          startDate: this.filters.dateRange?.[0] || undefined,
          endDate: this.filters.dateRange?.[1] || undefined,
          officeId: this.filters.officeId.length > 0 ? this.filters.officeId.join(',') : null,
          clientId: this.filters.clientId.length > 0 ? this.filters.clientId.join(',') : null,
          clientAdminId: this.filters.clientAdminId.length > 0 ? this.filters.clientAdminId.join(',') : null,
          page: this.pagination.currentPage,
          pageSize: this.pagination.perPage,
          sortField: this.sortProp,
          sortOrder: this.sortOrder,
        };
        const response = await reportsService.getClientComplianceReport(params);
        if (response.success && response.data) {
          this.reportData = response.data;
          this.pagination.total = response.total || response.data.length;
          this.pagination.totalPages = response.totalPages || Math.ceil(response.total / this.pagination.perPage);
          if (this.reportData.length === 0) {
            this.$notifyError('No expiring documents found for the selected filters');
          }
        } else {
          this.reportData = [];
          this.$notifyError('No expiring documents found');
        }
      } catch (error) {
        this.$notifyError('Failed to generate compliance report');
      } finally {
        this.loadingReport = false;
      }
    },

    async downloadClientComplianceCSV() {
      this.loadingDownload = true;
      try {
        const params = {
          startDate: this.filters.dateRange?.[0] || undefined,
          endDate: this.filters.dateRange?.[1] || undefined,
          officeId: this.filters.officeId.length > 0 ? this.filters.officeId.join(',') : null,
          clientId: this.filters.clientId.length > 0 ? this.filters.clientId.join(',') : null,
          clientAdminId: this.filters.clientAdminId.length > 0 ? this.filters.clientAdminId.join(',') : null,
        };
        const blob = await reportsService.downloadClientComplianceReport(params);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `client_compliance_report.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        this.$notifySuccess('Report downloaded successfully');
      } catch {
        this.$notifyError('Failed to download report');
      } finally {
        this.loadingDownload = false;
      }
    },

    getSubmissionQuarterYear() {
      if (!this.submissionSelectedQuarter) return null;
      const [year, qStr] = this.submissionSelectedQuarter.split('-');
      return { quarter: parseInt(qStr.replace('Q', '')), year: parseInt(year) };
    },

    getSubmissionParams() {
      const qy = this.getSubmissionQuarterYear();
      if (!qy) return null;
      return {
        quarter: qy.quarter,
        year: qy.year,
        clientId: this.submissionFilters.clientId.length > 0 ? this.submissionFilters.clientId.join(',') : undefined,
        payerType: this.submissionFilters.payerType.length > 0 ? this.submissionFilters.payerType.join(',') : undefined,
        supportCoordinatorId: this.submissionFilters.supportCoordinatorId.length > 0 ? this.submissionFilters.supportCoordinatorId.join(',') : undefined,
        status: this.submissionFilters.status.length > 0 ? this.submissionFilters.status.join(',') : undefined,
      };
    },

    async generateQuarterlySubmissionReport() {
      const baseParams = this.getSubmissionParams();
      if (!baseParams) return;
      this.loadingReport = true;
      try {
        const params = {
          ...baseParams,
          page: this.pagination.currentPage,
          pageSize: this.pagination.perPage,
          sortField: this.sortProp,
          sortOrder: this.sortOrder,
        };
        const response = await reportsService.getQuarterlySubmissionReport(params);
        if (response.success && response.data) {
          this.reportData = response.data;
          this.pagination.total = response.total || response.data.length;
          this.pagination.totalPages = response.totalPages || Math.ceil(response.total / this.pagination.perPage);
          if (this.reportData.length === 0) this.$notifyError('No DSPD/Private clients with shifts found for this quarter');
        } else {
          this.reportData = [];
          this.$notifyError('No data found for the selected quarter');
        }
      } catch (error) {
        this.$notifyError('Failed to generate quarterly submission report');
      } finally {
        this.loadingReport = false;
      }
    },

    async downloadQuarterlySubmissionCSV() {
      const params = this.getSubmissionParams();
      if (!params) {
        this.$notifyError('Please select a quarter');
        return;
      }
      this.loadingDownload = true;
      try {
        const blob = await reportsService.downloadQuarterlySubmissionReport(params);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `quarterly_submission_Q${params.quarter}_${params.year}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        this.$notifySuccess('Report downloaded successfully');
      } catch {
        this.$notifyError('Failed to download report');
      } finally {
        this.loadingDownload = false;
      }
    },

    downloadClientSideCSV(filename) {
      if (this.reportData.length === 0) {
        this.$notifyError('No data to export');
        return;
      }
      const cols = this.currentReportColumns;
      const header = cols.map(c => c.label).join(',');
      const rows = this.reportData.map(row => cols.map(c => {
        const val = row[c.key];
        return typeof val === 'string' && val.includes(',') ? `"${val}"` : (val ?? '');
      }).join(','));
      const csv = [header, ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${filename}_${this.filters.dateRange[0]}_to_${this.filters.dateRange[1]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      this.$notifySuccess('Report exported successfully');
    },
  },
};
</script>

<style scoped>
.page-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.download-btn {
  white-space: nowrap;
}

/* Compact Navigation Button */
.compact-nav-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.8);
  padding: 0;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  height: 40px;
  width: 40px;
  min-width: 40px;
}

.compact-nav-btn:hover {
  background: rgba(255, 255, 255, 0.12);
  border-color: rgba(255, 255, 255, 0.25);
  color: #fff;
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.compact-nav-btn:active {
  transform: translateY(0);
}

.compact-nav-btn i {
  font-size: 12px;
}

.filters-container {
  --primary-color: #6ba6cd;
  --primary-hover: #5a94b8;
  --background-glass: rgba(255, 255, 255, 0.08);
  --border-color: rgba(255, 255, 255, 0.12);
  --text-primary: #fff;
  --text-secondary: rgba(255, 255, 255, 0.7);
  --shadow-subtle: 0 2px 8px rgba(0, 0, 0, 0.1);

  display: flex;
  align-items: center;
  gap: 0.75rem;
  row-gap: 0.5rem;
  padding: 1rem;
  background: var(--background-glass);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  box-shadow: var(--shadow-subtle);
  transition: all 0.2s ease;
  flex-wrap: wrap;
  margin-bottom: 1rem;
}

.filters-container:hover {
  border-color: rgba(255, 255, 255, 0.18);
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.12);
}

.filter-group-compact {
  display: flex;
  align-items: center;
  min-width: 0;
  flex-shrink: 0;
}

/* Timesheet billing pre-upload data warnings */
.timesheet-warnings {
  background: rgba(230, 162, 60, 0.12);
  border: 1px solid rgba(230, 162, 60, 0.45);
  border-radius: 8px;
  padding: 0.75rem 1rem;
}

.timesheet-warnings-header {
  font-weight: 600;
  color: #e6a23c;
  margin-bottom: 0.4rem;
}

.timesheet-warnings ul {
  margin: 0;
  padding-left: 1.25rem;
  max-height: 180px;
  overflow-y: auto;
}

.timesheet-warnings li {
  font-size: 0.85rem;
}

.filter-group-compact.date-group {
  min-width: 320px;
  flex-grow: 1;
  max-width: 480px;
}

/* Prev/next arrows live inside the date group, flanking the picker */
.filter-group-compact.date-nav-group {
  gap: 0.5rem;
}

.date-nav-group .compact-date-picker {
  flex: 1;
  min-width: 0;
}

.compact-select {
  min-width: 150px;
}

/* Make report select wider */
.filters-container .filter-group-compact:first-child .compact-select {
  min-width: 300px;
}

/* Force line break */
.filter-break {
  flex-basis: 100%;
  height: 0;
}

.compact-date-picker {
  width: 100%;
}

::v-deep .compact-date-picker .el-input__inner {
  text-overflow: clip;
  white-space: nowrap;
}

.compact-btn {
  white-space: nowrap;
}

.search-wrapper {
  margin-bottom: 20px;
}

/* Date Range Picker Styling */
::v-deep .el-date-editor {
  width: 100%;
}

::v-deep .el-date-editor .el-input__inner {
  background-color: #656565 !important;
  border: 1px solid #4a5568;
  color: #ffffff;
}

::v-deep .el-date-editor .el-input__inner:focus {
  border-color: #4caf50;
}

::v-deep .el-date-editor .el-input__icon {
  color: #a0aec0;
}

::v-deep .el-date-editor--daterange {
  width: 100% !important;
}

::v-deep .el-date-editor--daterange .el-input__suffix {
  right: 10px !important;
}

::v-deep .el-range-separator {
  color: #ffffff !important;
  padding: 0 5px !important;
}

::v-deep .el-range-input {
  background-color: #656565 !important;
  color: #ffffff !important;
}

::v-deep .el-range__close-icon {
  right: 10px !important;
}

::v-deep .el-date-editor--daterange .el-range__icon {
  color: #a0aec0;
}


.responses-modal-body {
  padding: 1rem;
}

.question-answer-row {
  padding: 0.5rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.question-answer-row:last-child {
  border-bottom: none;
}

.question-text {
  color: #b0b0b0;
  font-size: 0.85rem;
  margin-bottom: 0.25rem;
  font-style: italic;
}

.answer-text {
  color: #e0e0e0;
  font-size: 0.9rem;
  padding-left: 0.5rem;
}

.answer-text.answer-yes,
.answer-text.answer-no {
  font-weight: 500;
  text-transform: capitalize;
}

@media (max-width: 991px) {
  .filters-container {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
    padding: 0.75rem;
  }

  .filter-group-compact,
  .filter-group-compact.date-group {
    min-width: unset;
    width: 100%;
    max-width: unset;
  }

  .compact-select,
  .compact-date-picker {
    width: 100%;
    min-width: unset;
  }
}

.billing-grand-total-row {
  background-color: #2c3e50 !important;
  color: #fff !important;
  font-weight: 700;
  font-size: 0.95rem;
}

.billing-grand-total-row td {
  color: #fff !important;
}

.billing-subtotal-row {
  background-color: #e8edf2 !important;
  font-weight: 600;
  color: #2c3e50;
  border-top: 2px solid #bdc3c7;
}
</style>
